import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";

import { StatCard, StatCardGrid, TrendsLineChart } from "../../common";
import { ReportDatePicker } from "src/components/dateFilter";
import { DATE_RANGES } from "src/constants/reports";
import { LeadAnalytics } from "./LeadAnalytics";
import { useLeadSummaryQuery } from "src/query/experiences/leadsAndReports/leads";
import { useLeadsTimeSeriesQuery } from "src/query/experiences/leadsAndReports/reports";
import { selectDateFilter } from "src/redux/selectors/experiences/leadsAndReports";
import { setDateFilter } from "src/redux/actions/experiences/leadsAndReports";
import {
  convertDatesToTimezone,
  dateKey,
} from "src/services/experiences/leadsAndReports/dateUtils";
import {
  DateRangeType,
  ReportDateFilter,
} from "src/types/experiences/leadsAndReports";
import { DEFAULT_REPORT_TYPE } from "../../constants";
import styles from "../../common/common.module.scss";
import reportStyles from "../reports/report.module.scss";

interface LeadsListProps {
  storyboardId: string;
}

export const LeadsList: React.FC<LeadsListProps> = ({ storyboardId }) => {
  const dispatch = useDispatch();
  const dateFilter = useSelector(selectDateFilter);

  const handleReportDateChange = useCallback(
    (filter: {
      reportDate: {
        type: string;
        startDate: ReportDateFilter["startDate"];
        endDate: ReportDateFilter["endDate"];
      };
    }) => {
      const reportDate = filter.reportDate;
      dispatch(
        setDateFilter({
          type: reportDate.type as DateRangeType,
          startDate: reportDate.startDate,
          endDate: reportDate.endDate,
        }),
      );
    },
    [dispatch],
  );

  const { data: summary, isLoading: summaryLoading } =
    useLeadSummaryQuery(storyboardId);

  const timeSeriesRequest = {
    storyboard: storyboardId,
    reportType: DEFAULT_REPORT_TYPE,
    ...convertDatesToTimezone(dateFilter ?? undefined),
  };
  const { data: timeSeries } = useLeadsTimeSeriesQuery(
    timeSeriesRequest,
    dateKey(dateFilter ?? undefined),
    Boolean(dateFilter),
  );
  const views = (
    Array.isArray(timeSeries) ? timeSeries : _.get(timeSeries, "items", [])
  ) as { key: string; value: number }[];

  return (
    <>
      <StatCardGrid>
        <StatCard
          icon="/images/experiences/reports/leads.svg"
          heading="Leads Captured"
          value={_.get(summary, "leads", 0) || 0}
          loading={summaryLoading}
          className={styles.statCardLeads}
        />
        <StatCard
          icon="/images/experiences/leads/accounts.svg"
          heading="Accounts Influenced"
          value={_.get(summary, "account", 0) || 0}
          loading={summaryLoading}
          className={styles.statCardLeads}
        />
        <StatCard
          icon="/images/experiences/reports/average-time.svg"
          heading="Conversion Rate"
          value={`${_.get(summary, "conversion", 0) || 0}%`}
          loading={summaryLoading}
          className={styles.statCardLeads}
        >
          {views?.length > 0 ? (
            <div className={styles.statChart}>
              <div className={styles.statChartCanvas}>
                <TrendsLineChart data={views} dataKey="value" height={90} />
              </div>
              <div className={styles.statChartLegend}>
                <span className={styles.dot}>●</span> Leads
              </div>
            </div>
          ) : (
            <></>
          )}
        </StatCard>
      </StatCardGrid>

      {dateFilter && (
        <ReportDatePicker
          dateRanges={DATE_RANGES}
          value={dateFilter}
          onChange={handleReportDateChange}
          filter={{ interval: "day" }}
          handleTimeLineChange={() => {}}
          className={reportStyles.datePickerFilter}
          showTimelineFilter={false}
        />
      )}

      <LeadAnalytics storyboardId={storyboardId} showProfile isLeadReportPage />
    </>
  );
};
